import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, computed, effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { injectAuthenticatedUser } from '@kinet-open-network/client/auth/data-access';
import { Customer } from '@kinet-open-network/client/customers/data-access';
import { ICustomer } from '@kinet-open-network/shared/data-access/model';
import { patchState, signalState } from '@ngrx/signals';
import { plainToInstance } from 'class-transformer';
import { firstValueFrom, map } from 'rxjs';
import {
  CUSTOMER_ID_HEADER_KEY,
  CUSTOMER_ID_STORAGE_KEY,
  DISABLE_CUSTOMER_CONTEXT,
} from './customer-context.tokens';

interface CustomerContextState {
  initialized: boolean;
  customer: Customer | null;
}

export function injectCurrentCustomer() {
  return inject(CustomerContextService).customer();
}

@Injectable({
  providedIn: 'root',
})
export class CustomerContextService {
  private http = inject(HttpClient);
  private user = injectAuthenticatedUser();
  private storage = localStorage;
  private state = signalState<CustomerContextState>({
    initialized: false,
    customer: null,
  });
  private customerIds = computed(
    () => (this.user()?.options?.['crmIds'] || []) as number[],
  );

  readonly state$ = toObservable(this.state);
  readonly customer = this.state.customer;
  readonly isMultiAccount = computed(
    () => this.customerIds().length > 1 || this.user()?.group?.master,
  );
  readonly isMasterAccount = computed(() => this.user()?.group?.master);

  constructor() {
    effect(() => {
      const customer = this.customer();
      if (customer) {
        this.storage.setItem(CUSTOMER_ID_STORAGE_KEY, customer.id.toString());
      }
    });
  }

  clearContext(uninit = false) {
    patchState(this.state, (state) => ({
      customer: null,
      initialized: uninit ? false : state.initialized,
    }));

    if (uninit) {
      this.storage.removeItem(CUSTOMER_ID_STORAGE_KEY);
    }
  }

  async setContext(customerIdOrEntity: number | Customer) {
    let customer: Customer | null = null;

    if (typeof customerIdOrEntity === 'number') {
      customer = await firstValueFrom(
        this.http
          .get<{ data: ICustomer }>(`/customers/${customerIdOrEntity}`, {
            headers: {
              [CUSTOMER_ID_HEADER_KEY]: customerIdOrEntity.toString(),
            },
            context: new HttpContext().set(DISABLE_CUSTOMER_CONTEXT, true),
          })
          .pipe(map((res) => plainToInstance(Customer, res.data))),
      );
    } else {
      customer = customerIdOrEntity;
    }

    patchState(this.state, {
      customer,
      initialized: true,
    });

    return customer;
  }

  async refreshContext() {
    const customerIds = this.customerIds();
    const isMaster = this.isMasterAccount();
    let customerId: number | null =
      customerIds.length === 1 ? customerIds[0] : null;

    const storedCustomerId = parseInt(
      this.storage.getItem(CUSTOMER_ID_STORAGE_KEY) || '',
      10,
    );

    if (
      storedCustomerId &&
      !isNaN(storedCustomerId) &&
      (customerIds.includes(storedCustomerId) || isMaster)
    ) {
      customerId = storedCustomerId;
    }

    if (customerId) {
      await this.setContext(customerId);
    } else {
      this.clearContext();
    }

    patchState(this.state, { initialized: true });
  }

  async initContext() {
    return this.refreshContext();
  }
}
